// useFileUploads
// --------------
// Custom React hook — owns the docs / invoices file-management surface for a
// single request: the docs[] / invoices[] arrays, the per-slot fileUploading
// flag map, the docLabels map, and the uploadFile handler that uploads to
// Supabase storage, refreshes the row from the DB to avoid clobbering
// concurrent uploads, writes back, and triggers the right notification.
//
// Extracted from RequestDetail.jsx (Phase D). Behaviour is identical.
// TypeScript source — useFileUploads.js is a thin re-export stub.

import React, { useState } from "react";
import { appendRequestNotes, getRequestFilesById, updateRequestById } from "../data/requests";
import { uploadInvoiceFile, uploadFile as uploadToBucket } from "../data/storage";
import { alertDialog, promptDialog } from "../services/dialogService";
import { validateUploadFile } from "../services/fileTypeGuard";
import { requestStamp } from "../services/requestStamp";
import type { DocEntry } from "../types/database.js";
import { createTask, sendEmail } from "../utils/actions";
import { logAudit } from "../utils/auditLog";
import { logger } from "../utils/logger";

interface FileUploadReq {
  id?: number | string | null;
  refNo?: string | null;
  invoices?: Array<string | null>;
  docs?: Array<DocEntry | null>;
  shelterName?: string | null;
  shelterCode?: string | null;
  shelterEmail?: string | null;
  state?: string | null;
  notes?: unknown[] | null;
}
interface FileUploadUser {
  shelterName?: string | null;
  // Needed by requestStamp to record WHICH SIDE (Zephyr or the service) wrote.
  role?: string | null;
}
interface UseFileUploadsArgs {
  req: FileUploadReq;
  user: FileUploadUser;
  isStaff: boolean;
  onSaveNoDb: (updatedReq: unknown) => void;
  showSaved: (msg: string, durationMs?: number) => void;
  /**
   * Optional. Updates RequestDetail's local notes state so a note written during an
   * invoice upload appears in the open Comments panel live (without a page reload).
   */
  setNotes?: (notes: unknown[]) => void;
}

export function useFileUploads({
  req,
  user,
  isStaff,
  onSaveNoDb,
  showSaved,
  setNotes,
}: UseFileUploadsArgs) {
  const [invoices, setInvoices] = useState<Array<string | null>>(
    req.invoices || Array(6).fill(null)
  );
  const [docs, setDocs] = useState<Array<DocEntry | null>>(req.docs || Array(6).fill(null));
  const [fileUploading, setFileUploading] = useState<Record<string, boolean>>({});
  const [docLabels, _setDocLabels] = useState<Record<string, string>>({});

  // Always fetch fresh docs+invoices from DB when request changes
  React.useEffect(
    function () {
      if (!req.id) return;
      getRequestFilesById(req.id).then(function (files) {
        if (files) {
          if (files.docs) setDocs(files.docs as Array<DocEntry | null>);
          if (files.invoices) setInvoices(files.invoices as Array<string | null>);
        }
      });
    },
    [req.id]
  );

  async function uploadFile(
    arr: Array<string | DocEntry | null>,
    setArr: (a: Array<string | DocEntry | null>) => void,
    i: number,
    f: File | null,
    isInvoice: boolean
  ) {
    if (!f) {
      var a = [...arr];
      a[i] = null;
      setArr(a);
      return;
    }
    // Content-aware type gate (PDF/JPG/PNG, extension AND magic bytes must agree).
    const valid = await validateUploadFile(f);
    if (!valid.ok) {
      await alertDialog({ message: valid.error });
      return;
    }
    var folder = isInvoice ? "invoices" : "docs";
    var safeName = `${Date.now()}_${f.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
    var path = `${req.refNo}/${folder}/${safeName}`;
    var key = `${folder}_${i}`;
    setFileUploading(function (p) {
      return Object.assign({}, p, { [key]: true });
    });
    const uploadRes = isInvoice
      ? await uploadInvoiceFile(path, f, { upsert: true, contentType: valid.contentType })
      : await uploadToBucket(path, f, { upsert: true, contentType: valid.contentType });
    setFileUploading(function (p) {
      return Object.assign({}, p, { [key]: false });
    });
    if (uploadRes.error) {
      logger.warn("Upload failed:", uploadRes.error.message);
      return;
    }
    // Always fetch fresh from DB first to avoid overwriting docs uploaded by other users
    var freshFiles = await getRequestFilesById(req.id);
    var freshField: "invoices" | "docs" = isInvoice ? "invoices" : "docs";
    var freshVal = freshFiles ? freshFiles[freshField] : null;
    var freshArr: Array<string | DocEntry | null> = (freshVal as Array<
      string | DocEntry | null
    > | null) ?? [...arr];
    // Find first empty slot at or after i
    var insertIdx = i;
    while (insertIdx < freshArr.length && freshArr[insertIdx]) insertIdx++;
    if (insertIdx >= freshArr.length) insertIdx = i; // fallback to requested slot
    var newArr = freshArr.slice();
    if (isInvoice) {
      // Private bucket (db-26): store the object PATH; rendering signs it on demand.
      newArr[insertIdx] = path;
    } else {
      const docLabel = docLabels[i] || "Other";
      // Private bucket (db-168): store the object PATH in `url`; rendering signs it
      // on demand with getFileSignedUrl (which also accepts a legacy full URL).
      newArr[insertIdx] = { url: path, label: docLabel, name: f.name };
    }
    // Update local state with full fresh array
    if (isInvoice) {
      setInvoices(newArr as Array<string | null>);
    } else {
      setDocs(newArr as Array<DocEntry | null>);
    }
    // Save to DB immediately
    var updateField = isInvoice ? { invoices: newArr } : { docs: newArr };
    await updateRequestById(req.id, Object.assign({}, updateField, requestStamp(user)));
    const newReq = Object.assign({}, req, updateField, {
      updatedBy: user.shelterName,
      updatedAt: new Date().toISOString(),
    });
    onSaveNoDb(newReq);
    showSaved(`${isInvoice ? "Invoice" : "Document"} uploaded and saved ✓`);
    // Email notification
    if (isInvoice) {
      // A shelter gets a moveable MANDATORY prompt to add a note for the invoicing
      // team (between the invoice landing and the email firing). The note is: (a) a
      // SILENT shelter comment on the request (no responder task, no email), and
      // (b) folded into the new_invoice email. Staff invoice uploads skip the prompt.
      let invoiceMessage = "";
      if (!isStaff) {
        const typed = await promptDialog({
          title: "Message for the invoicing team",
          message: `Add a note about "${f.name}" for the Zephyr invoicing team.`,
          placeholder: `e.g. Camp fee invoice for Term 3 — please process against ${req.refNo || "this request"}`,
          confirmLabel: "Send to Invoicing",
          minLength: 10,
          draggable: true,
        });
        invoiceMessage = typeof typed === "string" ? typed.trim() : "";
        // Silent shelter comment: note the invoice + file name + any message.
        const note = {
          author: user.shelterName,
          text: `Invoice added — ${f.name}${invoiceMessage ? `\n\n${invoiceMessage}` : ""}`,
          date: new Date().toLocaleString("en-AU"),
          ts: new Date().toISOString(),
          byRole: "shelter",
          tier: "shelter",
        };
        // Atomic append (db-200) — a shelter note (markUnread:false, no unread/task),
        // never a whole-array rewrite from a browser copy. docs/notes-atomic-append-plan.md.
        const noteRes = await appendRequestNotes(req.id, [note], {
          updatedBy: user.shelterName,
          markUnread: false,
        });
        const updatedNotes = noteRes.notes ?? [...(req.notes || []), note];
        onSaveNoDb(Object.assign({}, newReq, { notes: updatedNotes }));
        // Update RequestDetail's local notes state so the Comments panel (which reads
        // that state, not req.notes) shows the new note without a page refresh.
        if (setNotes) setNotes(updatedNotes);
      }
      sendEmail("new_invoice", {
        requestId: req.id,
        refNo: req.refNo,
        shelterCode: req.shelterCode || "",
        shelterName: req.shelterName,
        fileName: f.name,
        message: invoiceMessage,
        // Invoices are private (db-26) — the email links the request + shows the file
        // name; no direct file link. A send failure is surfaced centrally
        // (handleSendFailure: audit log + admin alert + a blocking dialog).
      });
    } else {
      if (isStaff) {
        // Staff uploaded a document — notify shelter
        if (req.shelterEmail) {
          sendEmail("file_to_shelter", {
            requestId: req.id,
            refNo: req.refNo,
            shelterName: req.shelterName,
            shelterEmail: req.shelterEmail,
            fileName: f.name,
          });
        }
      } else {
        // Shelter uploaded a document — create task for Responder
        createTask("new_file", {
          requestId: req.id,
          refNo: req.refNo,
          shelterName: req.shelterName,
          shelterCode: req.shelterCode || "",
          state: req.state,
          fileName: f.name,
        });
      }
    }
    logAudit("document_uploaded", {
      request_id: req.id,
      ref: req.refNo,
      file: f.name,
      by: user.shelterName,
    });
  }

  return {
    docs,
    setDocs,
    invoices,
    setInvoices,
    fileUploading,
    docLabels,
    uploadFile,
  };
}
